import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAsset, useExportAssetExcel, useExportAssetPdf } from '../hooks/useAssets.ts';
import { ArrowLeft, Calendar, Package, User, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';
import { PermissionPage } from '../components/PermissionGate';
import { PERMISSIONS } from '../utils/permissions';

const AssetDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: assetData, isLoading, error } = useAsset(Number(id));
  const exportExcel = useExportAssetExcel();
  const exportPdf = useExportAssetPdf();
  const asset = assetData?.data;

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: 'bg-green-100 text-green-800',
      sold: 'bg-blue-100 text-blue-800',
      disposed: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-red-600 text-lg">Error loading asset details. Please try again.</p>
            <button
              onClick={() => navigate('/assets')}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Assets
            </button>
          </div>
        </div>
      </div>
    );
  }

  const members = asset.members || [];
  const totalPercentage = members.reduce((sum, m) => sum + Number(m.percentage || 0), 0);

  const handleExportExcel = () => {
    exportExcel.mutate(Number(id), {
      onSuccess: () => {
        toast.success('Asset exported to Excel successfully');
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error) || 'Failed to export asset to Excel');
      }
    });
  };

  const handleExportPdf = () => {
    exportPdf.mutate(Number(id), {
      onSuccess: () => {
        toast.success('Asset exported to PDF successfully');
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error) || 'Failed to export asset to PDF');
      }
    });
  };

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_ASSETS}>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/assets')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Assets
          </button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{asset.name}</h1>
              {asset.description && (
                <p className="mt-2 text-gray-600">{asset.description}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(asset.status)}
              
              {/* Export Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExportExcel}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  title="Export to Excel"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel
                </button>
                <button
                  onClick={handleExportPdf}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  title="Export to PDF"
                >
                  <FileText className="w-4 h-4" />
                  PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Asset Details Card */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Asset Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start space-x-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Value</p>
                  <p className="text-lg font-semibold text-gray-900">
                    KSh {Number(asset.total_value).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Purchase Date</p>
                  <p className="text-lg font-semibold text-gray-900">{asset.purchase_date}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <User className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Owners</p>
                  <p className="text-lg font-semibold text-gray-900">{members.length}</p>
                </div>
              </div>
            </div>

            {asset.notes && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-500 mb-2">Notes</p>
                <p className="text-gray-700">{asset.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Member Ownership Card */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Member Ownership</h2>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-500">Total Ownership:</span>
                <span className={`text-lg font-bold ${Math.abs(totalPercentage - 100) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalPercentage.toFixed(2)}%
                </span>
              </div>
            </div>

            {members.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No members assigned to this asset</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="responsive-table min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Member
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phone Number
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ownership %
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {members.map((member, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td data-label="Member" className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {member.member_name || 'Unknown Member'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td data-label="Phone Number" className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {member.member_phone || 'N/A'}
                          </div>
                        </td>
                        <td data-label="Ownership %" className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="inline-flex items-center">
                            <div className="mr-3 w-32 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${member.percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-semibold text-gray-900">
                              {Number(member.percentage).toFixed(2)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Metadata */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            Created on {new Date(asset.created_at).toLocaleDateString()} 
            {asset.updated_at && asset.updated_at !== asset.created_at && (
              <> • Last updated on {new Date(asset.updated_at).toLocaleDateString()}</>
            )}
          </p>
        </div>
      </div>
    </div>
    </PermissionPage>
  );
};

export default AssetDetailsPage;

